var _ = require('lodash'),
  ApiError = require('../models/errors/midas/apiError'),
  cache = require('../models/cache'),
  CONST_ERRORS = require('../constants/errors'),
  CONST_EVENTS = require('../constants/events'),
  ErrorResponse = require('../models/errors/midas/errorResponse'),
  GeneralError = require('../models/errors/generalError'),
  log = require('../libraries/logger').getLoggerInstance('Base.GeneralUtility'),
  MalformedJwtError = require('../models/errors/malformedJwtError'),
  passiveEvents = require('../events/eventObserver').passive,
  responseMapper = require('./responseMapper'),
  ServiceError = require('../models/errors/midas/serviceError'),
  UserCanceledPayment = require('../models/errors/userCanceledPayment'),
  uuid = require('uuid');

var publicMethods = {
  /**
   * A helper to determine what version of IE is being used
   * @returns {number} IE Version number detected
   */
  ieVersion: function () {
    if (document.documentMode) {
      return document.documentMode;
    } else {
      for (var i = 7; i > 0; i--) {
        var div = document.createElement('div');
        div.innerHTML = '<!--[if IE ' + i + ']><span></span><![endif]-->';

        if (div.getElementsByTagName('span').length) {
          return i;
        }
      }
    }

    return undefined;
  },
  /**
   * A helper to determine if an object is the window object
   * @param {object} variable The object to test as window
   * @return {boolean}
   */
  isWindow: function (variable) {
    return variable && variable.document && variable.location && variable.alert && variable.setInterval;
  },
  /**
   * A general error handler to parse different types of errors in the same way.
   * @param {object} error The error object that was caught.
   * @param {number=} defaultErrorNumber - The default error number that should be returned to the merchant if the error object doesn't contain one
   * @param {string=} defaultErrorDescription - The default error description that should be returned to the merchant if an error description was not provided within the error object
   * @param {string=} rawJwt - The raw response JWT from Centinel API if the error was generated by an API request. This value will be returned to the merchant for validation. If this value is available always pass it.
   */
  handleError: function (error, defaultErrorNumber, defaultErrorDescription, rawJwt) {
    // NEXTREV: Reduce Complexity
    var errorResponse, errorJwt;

    if (defaultErrorNumber === undefined) {
      defaultErrorNumber = CONST_ERRORS.GENERAL.NUMBER;
    }
    if (defaultErrorDescription === undefined) {
      defaultErrorDescription = CONST_ERRORS.GENERAL.DESCRIPTION;
    }

    if (error instanceof ApiError || error instanceof ServiceError) {
      log.debug('Error detected: ' + JSON.stringify(error));
      errorResponse = error;
      errorJwt = error.originalJwt;
      delete error.originalJwt;

    } else if (error instanceof ErrorResponse) {
      log.debug('ErrorResponse detected: ' + JSON.stringify(error));
      errorResponse = error;

    } else if (error instanceof UserCanceledPayment) {
      log.debug('User Canceled payment: ' + JSON.stringify(error));
      errorResponse = error;
      defaultErrorNumber = defaultErrorNumber || error.ErrorNumber;
      defaultErrorDescription = defaultErrorDescription || error.ErrorDescription;

    } else if (error instanceof MalformedJwtError || error instanceof GeneralError) {
      errorResponse = error;

    } else if (error instanceof Error) {
      // JS Exceptions
      log.error('unexpected Exception: ' + error.message);
      errorResponse = new GeneralError(defaultErrorNumber, error.message);
    } else {
      // Anything else we didn't account for
      log.error('unexpected Exception: ' + JSON.stringify(error));
      errorResponse = new GeneralError(defaultErrorNumber, defaultErrorDescription);
    }

    if (errorResponse.ErrorNumber === undefined) {
      errorResponse.ErrorNumber = defaultErrorNumber;
    }
    if (errorResponse.ErrorDescription === undefined || errorResponse.ErrorDescription === '') {
      errorResponse.ErrorDescription = defaultErrorDescription;
    }

    if (errorJwt === undefined) {
      if (rawJwt !== undefined) {
        errorJwt = rawJwt;
      } else if (error instanceof Object && 'rawJwt' in error && error.rawJwt !== undefined) {
        errorJwt = error.rawJwt;
      }
    }

    passiveEvents.publish(CONST_EVENTS.VALIDATED, responseMapper.map.midas.to.merchantResponse(errorResponse), errorJwt);
  },
  /**
   * Converts a string in all lower case to have the first letter captialized. This function does not account for any other case but the string being in all lower, meaning it will not force all other characters to lower case. So for example it will return 'VICTORIA' as 'VICTORIA', but it will convert 'victoria' to 'Victoria'.
   * @param {string} string - The string that should be mutated
   * @returns {string} Capitalized version of the original input string
   */
  toCapitalize: function (string) {
    return string.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1);
    });
  },
  /**
   * Given a CSV this function will return an array of strings. Returns an empty array if invalid input is found. If no comma is found on the input an array containing a single element will be returned
   * @param {string} csv - The CSV string to convert to an array of strings
   * @returns {Array} an array of string(s)
   */
  convertCsvToArray: function (csv) {
    if (csv !== undefined && typeof csv === 'string') {
      return csv.split(/\s*,\s*/);
    }
    return [];
  },
  /**
   * Generates a Tid
   * @returns {string} a guid Tid value
   */
  getTid: function () {
    return uuid.v4();
  },
  /**
   * Crosss Browser helper function to determine the domain that Songbird is currently running on
   * @returns {String} Domain including the protocol. IE: 'https://my.site.com'
   */
  getCurrentDomain: function () {
    if (window.location.origin !== undefined) {
      return window.location.origin;
    } else {
      return window.location.protocol + '//' + window.location.host;
    }
  },
  /**
   * Given a fully qualified URL this utility will return back the origin value. The origin includes: protocol + domain + port
   * @param {String} fullyQualifiedUrl
   * @returns {String} Origin value (protocol + domain + port) IE: https://cardinalcommerce.com:8080 or undefined if unable to parse
   */
  getOriginFromFullyQualifiedUrl: function (fullyQualifiedUrl) {
    if (fullyQualifiedUrl) {
      var urlParts = fullyQualifiedUrl.split('/');
      if (urlParts.length > 2) {
        return urlParts[0] + "//" + urlParts[2];
      }
    }
    return undefined;
  },
  /**
   * Helper utility to add new origins to the post message white list, using a fully qualified url. This utility will only add an origin if it is not found in the existing white list
   * @param {String} fullyQualifiedUrl The full url that the origin needs to be added to our white list
   */
  addOriginToPostMessageWhiteList: function (fullyQualifiedUrl) {
    var origin = publicMethods.getOriginFromFullyQualifiedUrl(fullyQualifiedUrl),
      foundOrigin;
      
    if (origin !== undefined) {
      foundOrigin = _.find(cache.urls.postMessageWhiteList, function(validOrigin){
        return validOrigin === origin;
      });
      if(foundOrigin === undefined){
        cache.urls.postMessageWhiteList.push(origin);
      }      
    }

  }
};

module.exports = publicMethods;


//////////////////
// WEBPACK FOOTER
// ./utilities/generalUtility.js
// module id = 32
// module chunks = 0 5 20